/**
 * The `Media` tab: a dense, newest-first grid of every photo and GIF, with a
 * full-screen viewer with arrow and swipe navigation opened on tap.
 */
import { useChatListMedia } from "@sportsmet/messaging-sdk/private";
import { Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { MediaViewer, type typeViewerItem } from "./MediaViewer";
import { useChatMediaUrls } from "./useChatMediaUrls";

export type typeMediaTabProps = {
  chatId: string;
};

const MEDIA_PAGE_LIMIT = 200;

export const MediaTab = (props: typeMediaTabProps): ReactNode => {
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  const chatListMedia = useChatListMedia({
    chatId: props.chatId,
    limit: MEDIA_PAGE_LIMIT,
  });
  const rows = chatListMedia.data?.data?.items ?? [];
  const resolved = useChatMediaUrls(rows.map((row) => row.mediaObjectId));

  if (chatListMedia.isLoading) {
    return (
      <div
        data-testid="chat-details-media-loading"
        className="flex flex-col gap-3"
      >
        <Skeleton shape="card" label="Loading media" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        data-testid="chat-details-media-empty"
        className="flex flex-col items-center gap-1 py-10 text-center"
      >
        <p className="text-body-strong text-ink">No photos or GIFs yet</p>
      </div>
    );
  }

  const viewerItems: typeViewerItem[] = [];
  const viewerIndexByMessageId = new Map<string, number>();
  for (const row of rows) {
    const media = resolved.get(row.mediaObjectId);
    if (media === null || media === undefined) continue;
    viewerIndexByMessageId.set(row.messageId, viewerItems.length);
    viewerItems.push({
      messageId: row.messageId,
      largeUrl: media.largeUrl,
      sentAtIso: row.sentAtIso,
    });
  }

  return (
    <>
      <div
        data-testid="chat-details-media"
        className="grid grid-cols-3 gap-1 md:grid-cols-4"
      >
        {rows.map((row) => {
          const media = resolved.get(row.mediaObjectId);
          if (media === null || media === undefined) {
            return (
              <div
                key={row.messageId}
                data-testid="media-tile-inert"
                className="aspect-square bg-surface-secondary"
              />
            );
          }
          const targetIndex = viewerIndexByMessageId.get(row.messageId);
          return (
            <button
              key={row.messageId}
              type="button"
              data-testid="media-tile"
              className="aspect-square overflow-hidden bg-surface-secondary"
              onClick={() => {
                if (targetIndex !== undefined) setViewerIndex(targetIndex);
              }}
            >
              <img
                src={media.thumbnailUrl}
                alt=""
                className="h-full w-full object-cover"
              />
            </button>
          );
        })}
      </div>
      {viewerIndex !== null ? (
        <MediaViewer
          items={viewerItems}
          index={viewerIndex}
          onIndexChange={setViewerIndex}
          onClose={() => {
            setViewerIndex(null);
          }}
        />
      ) : null}
    </>
  );
};
